import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { groupMovements, getConvertedTotal } from '../api/endpointsBackend';
import { useDisplayCurrency } from '../contexts/DisplayCurrencyContext';
import { mergeRateSources } from '../utils/rateSources';
import {
  dimensionsForLevels, granularityFor, groupOrderingFor,
  rowDimensionFor, isDateGrouping, splitsByCurrency,
  GROUPING_NONE, GROUPING_CURRENCY, GROUPING_DAY, GROUPING_MONTH, GROUPING_YEAR,
  GROUPING_GOAL,
} from './useDisplaySettings';

/**
 * Ceiling on synthesized empty buckets (rev 5).
 *
 * Two years of daily buckets. Past it the fill is skipped whole rather than
 * truncated: a calendar missing an arbitrary tail is worse than no calendar,
 * because the gap it leaves looks exactly like the gap it was meant to fix.
 */
const MAX_EMPTY_GROUPS = 800;

/**
 * MI-139 — group headers for the grouped Movements view.
 *
 * One aggregate query returns key + COUNT + SUM per group and not a single
 * movement row; rows are hydrated separately. That split is what makes the
 * view cost the same whether the period holds 600 movements or 60,000.
 */

/** Group headers come in pages. Category/account groupings are bounded by how
 *  many exist, but grouping by a date dimension over an all-time range is not:
 *  a few years of data yields thousands of day buckets, and a flat cap would
 *  drop the rest with no sign that anything was missing. */
/**
 * Group headers per page.
 *
 * Day groups open expanded — that view is the ungrouped list, and nothing
 * should be hidden on arrival — so every group on the page needs its rows up
 * front. Fewer per page keeps that affordable, and mirrors how the ungrouped
 * list paginates a handful of dates at a time. Other groupings open collapsed,
 * so more headers cost nothing.
 */
export function groupPageFor(grouping) {
  return grouping === GROUPING_DAY ? 15 : 50;
}

/**
 * How the *server* should order groups before the cap bites.
 *
 * Deliberately independent of the user's ordering choice: groups are merged
 * and re-sorted client-side anyway, so the server order only decides which
 * groups survive `limit`. Keeping it stable means changing the ordering
 * re-sorts in memory instead of refetching — and, more importantly, it breaks
 * the loop where fetching produced `currencyCount`, which changed `ordering`,
 * which refetched, running every prefetch a second time.
 */
function serverOrderingFor(grouping) {
  return isDateGrouping(grouping) ? 'date_desc' : 'amount_desc';
}

/**
 * A key that is unique per group *and* sorts correctly.
 *
 * `month` is the trap: `extract('month')` yields 1..12, so keying on the value
 * alone both merges December 2023 into December 2026 and sorts every December
 * above every August regardless of year. The year has to travel with it.
 */
function sortKeyOf(entry) {
  if (entry.grouping === GROUPING_MONTH) {
    return (entry.year ?? 0) * 100 + Number(entry.value ?? 0);
  }
  if (entry.grouping === GROUPING_YEAR) return Number(entry.value ?? 0);
  return entry.value ?? '';
}

/** The dimension's value on an aggregate row, whatever its shape. */
function valueOf(row, grouping) {
  const dim = rowDimensionFor(grouping);
  if (dim === 'period') return row.period ?? null;
  if (dim === 'month') return row.month ?? null;
  if (dim === 'year') return row.year ?? null;
  return row[dim] ?? null;
}

/** Human label carried alongside the id, when the backend joins one in. */
function labelOf(row, grouping) {
  if (grouping === 'category') return row.category_name ?? null;
  if (grouping === 'account') return row.account_name ?? null;
  if (grouping === 'goal') return row.goal_name ?? null;
  return null;
}

/**
 * Collapse the per-currency aggregate rows into one entry per dimension value.
 *
 * The query must group by currency — that constraint is what stops SQL summing
 * COP and USD into one number — but rendering those rows directly fragments
 * every group: a four-currency workspace shows `EXPENSES` four times before it
 * shows a second category. So the currency axis is folded here, and a group
 * that spans currencies exposes no total at all rather than a meaningless one.
 */
function mergeByDimension(rows, grouping, keyExtra) {
  const merged = new Map();

  rows.forEach(row => {
    const value = valueOf(row, grouping);
    // December 2023 and December 2026 are different groups; the bare month
    // number would collapse them into one.
    const yearPart = grouping === GROUPING_MONTH ? `${row.year}-` : '';
    const key = `${keyExtra ? `${keyExtra(row)}|` : ''}${yearPart}${value}`;
    let entry = merged.get(key);
    if (!entry) {
      entry = {
        key,
        grouping,
        value,
        label: labelOf(row, grouping),
        year: row.year ?? null,
        count: 0,
        currencies: [],
        ignoredCurrencies: [],
        totalsByCurrency: {},
        amountsByCurrency: {},
        latestDate: null,
        rateSources: [],
      };
      merged.set(key, entry);
    }
    // The rates behind a converted bucket (MI-329); raw rows carry none.
    entry.rateSources = mergeRateSources(entry.rateSources, row.rate_sources);
    // MI-315 — a slice whose movements are all ignored arrives with a zero
    // count. It keeps its group on the list and counts toward nothing, its
    // currency included: one ignored USD row must not turn a COP day into a
    // multi-currency group.
    if (row.num_movements === 0) {
      if (!entry.ignoredCurrencies.includes(row.currency)) entry.ignoredCurrencies.push(row.currency);
      return;
    }
    entry.count += row.num_movements ?? 0;
    if (!entry.currencies.includes(row.currency)) entry.currencies.push(row.currency);
    entry.totalsByCurrency[row.currency] =
      (entry.totalsByCurrency[row.currency] || 0) + (row.total ?? 0);
    entry.amountsByCurrency[row.currency] =
      (entry.amountsByCurrency[row.currency] || 0) + (row.total_amount ?? 0);
  });

  return [...merged.values()].map(({ ignoredCurrencies, ...entry }) => {
    // A group of ignored movements alone still needs a unit for its zero.
    const currencies = entry.currencies.length ? entry.currencies : ignoredCurrencies;
    const single = currencies.length === 1;
    return {
      ...entry,
      currencies,
      // A total only exists when there is one currency to express it in.
      // Everything downstream keys on `currency === null` to decide between
      // rendering an amount and rendering the multi-currency indicator.
      currency: single ? currencies[0] : null,
      total: single ? entry.totalsByCurrency[currencies[0]] ?? 0 : null,
      // Plain sum of what moved, for groups whose net is zero by definition.
      totalAmount: single ? entry.amountsByCurrency[currencies[0]] ?? 0 : null,
    };
  });
}

/** Sort merged groups client-side; SQL ordered the pre-merge rows.
 *
 *  A goal grouping pins its NULL bucket — "No goal" — last whatever the
 *  ordering says: it is the absence of a goal, not one more goal, and the
 *  reader looks for their goals at the top.
 */
function sortMerged(groups, ordering) {
  const byMagnitude = (a, b) => Math.abs(b.total ?? 0) - Math.abs(a.total ?? 0);
  const byChronoDesc = (a, b) => {
    const ka = sortKeyOf(a);
    const kb = sortKeyOf(b);
    if (typeof ka === 'number' && typeof kb === 'number') return kb - ka;
    return String(kb).localeCompare(String(ka), undefined, { numeric: true });
  };
  const byLabel = (a, b) =>
    String(a.label ?? a.value ?? '').localeCompare(String(b.label ?? b.value ?? ''));

  const within = (compare) => (a, b) => {
    if (a.grouping === GROUPING_GOAL || b.grouping === GROUPING_GOAL) {
      const aEmpty = a.value == null;
      const bEmpty = b.value == null;
      if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
    }
    return compare(a, b);
  };

  const sorted = [...groups];
  switch (ordering) {
    case 'amount_asc':  return sorted.sort(within((a, b) => -byMagnitude(a, b)));
    case 'date_asc':    return sorted.sort(within((a, b) => -byChronoDesc(a, b)));
    case 'date_desc':   return sorted.sort(within(byChronoDesc));
    case 'name_asc':    return sorted.sort(within(byLabel));
    default:            return sorted.sort(within(byMagnitude));
  }
}

/**
 * Every date bucket a range covers, coarse → fine (rev 5).
 *
 * Parsed by hand rather than through `new Date(iso)`: that constructor reads a
 * bare `YYYY-MM-DD` as UTC midnight and then renders it in local time, which
 * west of Greenwich turns the 1st into the previous month's 31st — a whole
 * bucket off, in the one place a bucket boundary is the entire point.
 */
function bucketsInRange(grouping, startDate, endDate) {
  const parse = (iso) => {
    const [y, m, d] = String(iso).split('-').map(Number);
    return (y && m && d) ? { y, m, d } : null;
  };
  const from = parse(startDate);
  const to = parse(endDate);
  if (!from || !to) return null;

  const out = [];
  if (grouping === GROUPING_YEAR) {
    for (let y = from.y; y <= to.y; y += 1) out.push({ value: y, year: y });
    return out;
  }
  if (grouping === GROUPING_MONTH) {
    let y = from.y;
    let m = from.m;
    while (y < to.y || (y === to.y && m <= to.m)) {
      out.push({ value: m, year: y });
      m += 1;
      if (m > 12) { m = 1; y += 1; }
    }
    return out;
  }
  if (grouping === GROUPING_DAY) {
    const pad = (n) => String(n).padStart(2, '0');
    // UTC arithmetic all the way through, so the DST hour never shifts a day.
    const cursor = Date.UTC(from.y, from.m - 1, from.d);
    const last = Date.UTC(to.y, to.m - 1, to.d);
    for (let t = cursor; t <= last; t += 86400000) {
      const dt = new Date(t);
      const y = dt.getUTCFullYear();
      out.push({
        value: `${y}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`,
        year: y,
      });
      if (out.length > MAX_EMPTY_GROUPS) return out;  // caller sees the overflow
    }
    return out;
  }
  return null;
}

/** The key `mergeByDimension` would have produced for this bucket. */
function keyForBucket(grouping, bucket) {
  return grouping === GROUPING_MONTH
    ? `${bucket.year}-${bucket.value}`
    : `${bucket.value}`;
}

/**
 * Fill the gaps a `GROUP BY` cannot report.
 *
 * SQL returns the buckets that have rows; a February with no USD income
 * produces no row at all, so the list skips it silently. That absence is
 * often the finding, so the missing buckets are synthesized here —
 * client-side, and opt-in.
 *
 * Where the missing values come from depends on the dimension. A calendar one
 * derives them from the range the filter bar already carries. Every other one
 * has to be told: which categories, accounts, types or currencies exist is a
 * fact about the workspace, not about the range, so the page passes them in as
 * `candidates`.
 *
 * Day is the exception at both ends — a day with nothing in it is not a
 * finding, it is most days.
 */
function withEmptyBuckets(parents, {
  grouping, startDate, endDate, hasMoreGroups, zeroCurrency, candidates,
}) {
  if (parents.length === 0 || grouping === GROUPING_DAY) return parents;

  let buckets;
  if (isDateGrouping(grouping)) {
    // While more group pages exist, only the span already on screen is safe to
    // fill: a month absent from page 1 may simply be waiting on page 2, and
    // drawing it as empty would state something false.
    const bounds = hasMoreGroups
      ? spanOf(grouping, parents)
      : { start: startDate, end: endDate };
    if (!bounds?.start || !bounds?.end) return parents;
    buckets = bucketsInRange(grouping, bounds.start, bounds.end);
  } else {
    // Same honesty rule, with no span to fall back on: a category absent from
    // this page may be on the next one, so an unfinished list fills nothing.
    if (!candidates || candidates.length === 0 || hasMoreGroups) return parents;
    buckets = candidates.map(c => ({ value: c.value, label: c.label, year: null }));
  }
  if (!buckets || buckets.length > MAX_EMPTY_GROUPS) return parents;

  const seen = new Set(parents.map(p => p.key));
  const filled = [...parents];
  buckets.forEach(bucket => {
    const key = keyForBucket(grouping, bucket);
    if (seen.has(key)) return;
    filled.push({
      key,
      grouping,
      value: bucket.value,
      // A date bucket names itself; a category or account arrives with the
      // name the page looked up, since no aggregate row carries it.
      label: bucket.label ?? null,
      year: bucket.year,
      count: 0,
      currencies: zeroCurrency ? [zeroCurrency] : [],
      totalsByCurrency: {},
      amountsByCurrency: {},
      // A zero still needs a unit. Where the set spans currencies there is
      // none to borrow, and the header falls back to a bare 0 — the same
      // reason a multi-currency group shows no total.
      currency: zeroCurrency,
      total: 0,
      totalAmount: 0,
      isEmpty: true,
    });
  });
  return filled;
}

/** The first and last calendar day the loaded buckets touch. */
function spanOf(grouping, parents) {
  const pad = (n) => String(n).padStart(2, '0');
  const edges = parents.map(p => {
    if (grouping === GROUPING_YEAR) return [`${p.value}-01-01`, `${p.value}-12-31`];
    if (grouping === GROUPING_MONTH) {
      const y = p.year;
      const m = Number(p.value);
      const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
      return [`${y}-${pad(m)}-01`, `${y}-${pad(m)}-${pad(lastDay)}`];
    }
    return [String(p.value), String(p.value)];
  });
  if (edges.length === 0) return null;
  return {
    start: edges.reduce((a, e) => (e[0] < a ? e[0] : a), edges[0][0]),
    end: edges.reduce((a, e) => (e[1] > a ? e[1] : a), edges[0][1]),
  };
}

/**
 * The window an empty *subgroup* may be synthesized in.
 *
 * A subgroup's reach is its parent's, never the whole filter range: the empty
 * months of 2025 are the months 2025 covers, clipped to what the filter asked
 * for. When the parent is not a date at all (a category, say) it constrains
 * nothing, and the filter range stands on its own.
 */
function childBounds(parent, grouping, startDate, endDate) {
  const span = isDateGrouping(grouping) ? spanOf(grouping, [parent]) : null;
  const later = (a, b) => (!a ? b : !b ? a : (a > b ? a : b));
  const earlier = (a, b) => (!a ? b : !b ? a : (a < b ? a : b));
  return {
    start: later(span?.start, startDate),
    end: earlier(span?.end, endDate),
  };
}

/**
 * Fold a set of merged entries into one — the header of a swimlane, which is
 * the same subgroup value seen across every column (rev 5).
 */
function foldEntries(entries, { key, grouping, value, label, year }) {
  const fold = {
    key, grouping, value, label, year,
    count: 0,
    currencies: [],
    totalsByCurrency: {},
    amountsByCurrency: {},
    isConverted: entries.some(e => e.isConverted),
    rateSources: mergeRateSources(...entries.map(e => e.rateSources)),
  };
  // An entry of ignored movements alone lends its currency only to a lane
  // with nothing else in it, as in `mergeByDimension` (MI-315).
  const counted = entries.filter(e => e.count > 0 || e.isEmpty);
  (counted.length ? counted : entries).forEach(e => {
    fold.count += e.count ?? 0;
    (e.currencies || []).forEach(c => {
      if (!fold.currencies.includes(c)) fold.currencies.push(c);
    });
    if (e.currency) {
      fold.totalsByCurrency[e.currency] =
        (fold.totalsByCurrency[e.currency] || 0) + (e.total ?? 0);
      fold.amountsByCurrency[e.currency] =
        (fold.amountsByCurrency[e.currency] || 0) + (e.totalAmount ?? 0);
    }
  });
  const single = fold.currencies.length === 1;
  return {
    ...fold,
    currency: single ? fold.currencies[0] : null,
    total: single ? fold.totalsByCurrency[fold.currencies[0]] ?? 0 : null,
    totalAmount: single ? fold.amountsByCurrency[fold.currencies[0]] ?? 0 : null,
  };
}

/** The converted total of each group, keyed exactly like the merged groups.
 *
 * MI-318. `/movements/group` still decides which groups exist, in what order and
 * with which labels — untouched. This only replaces the *number* in the header,
 * which the per-currency split otherwise leaves blank whenever a group spans
 * more than one currency. The keys line up because converted buckets carry the
 * same dimension fields, so the very same `mergeByDimension` builds them.
 */
function convertedTotalsByKey(buckets, grouping, displayCurrency) {
  const map = new Map();
  // Every converted bucket is already denominated in the display currency, so
  // folding them has to *sum* across the source-currency axis — which is
  // exactly what `mergeByDimension` refuses to do when it sees several
  // currencies. Relabelling them tells it there is only one. The exception is
  // the currency level itself, where the source currency is the group's key and
  // must survive.
  const rows = grouping === GROUPING_CURRENCY
    ? (buckets || [])
    : (buckets || []).map(b => ({ ...b, currency: displayCurrency }));
  // The net AND the magnitude: a Transfer group nets to zero by definition, so
  // what it displays is `total_amount`. Without carrying that, the Transfer
  // group kept an unconverted per-currency figure above fully converted rows.
  // `mergeByDimension` already folds `total_amount` into `amountsByCurrency`,
  // under the key `undefined` — converted buckets have no currency axis left.
  mergeByDimension(rows, grouping).forEach((e) => {
    map.set(e.key, {
      total: e.total,
      totalAmount: e.amountsByCurrency?.[e.currencies?.[0]] ?? Math.abs(e.total ?? 0),
      rateSources: e.rateSources,
    });
  });
  return map;
}

/**
 * The groups two reads of the aggregates disagree on — the ones an edit
 * touched.
 *
 * Whatever moved a movement into, out of or within a group changes that
 * group's count or totals, so comparing the rows each group was built from
 * names exactly the groups whose window has to be read again, whichever
 * dimension the view groups by. A group present in only one read counts.
 */
function changedGroupKeys(before, after, grouping) {
  const signatures = (rows) => {
    const slices = new Map();
    rows.forEach(row => {
      // The same key `mergeByDimension` gives the group.
      const key = `${grouping === GROUPING_MONTH ? `${row.year}-` : ''}${valueOf(row, grouping)}`;
      if (!slices.has(key)) slices.set(key, []);
      slices.get(key).push(JSON.stringify(row));
    });
    slices.forEach((list, key) => slices.set(key, list.sort().join('|')));
    return slices;
  };
  const was = signatures(before);
  const now = signatures(after);
  return [...new Set([...was.keys(), ...now.keys()])]
    .filter(key => was.get(key) !== now.get(key));
}

export default function useGroupedMovements({
  enabled, grouping, subgrouping, ordering, filters, filterGroups,
  groupsOperator, startDate, endDate, showEmptyGroups = false,
  emptyCandidatesFor,
}) {
  const { displayCurrency } = useDisplayCurrency();
  const [convertedBuckets, setConvertedBuckets] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  // Bumped by `refetch`. Both queries below depend on it, because a refresh
  // that re-read only the aggregates left the *counts* right and the *totals*
  // stale: with a display currency active the headline figure comes from the
  // conversion request, not from `/movements/group`.
  const [reloadToken, setReloadToken] = useState(0);
  // Bumped by `refreshHeaders` for the converted-figure request alone: the
  // aggregates it re-reads itself, and only the totals under a display
  // currency need this second read.
  const [convertedToken, setConvertedToken] = useState(0);
  // Per group key, how many times an edit has changed that group. A group
  // reads its rows again when its own number moves — and no other does.
  const [groupVersions, setGroupVersions] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreGroups, setHasMoreGroups] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const refreshAbortRef = useRef(null);
  const pagesRef = useRef(1);
  // What `refreshHeaders` compares against and the reload it must not outlive.
  const rawRowsRef = useRef(rawRows);
  rawRowsRef.current = rawRows;
  const reloadTokenRef = useRef(reloadToken);
  reloadTokenRef.current = reloadToken;

  const load = useCallback(async () => {
    if (!enabled) {
      setRawRows([]);
      return;
    }
    const dimensions = dimensionsForLevels(grouping, subgrouping);
    if (!dimensions) return;

    refreshAbortRef.current?.abort();
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    try {
      pagesRef.current = 1;
      const pageSize = groupPageFor(grouping);
      const data = await groupMovements(
        filters, dimensions, startDate, endDate, filterGroups, groupsOperator,
        granularityFor(grouping) || granularityFor(subgrouping),
        {
          orderBy: serverOrderingFor(grouping),
          // One over the page, so a full page tells us more exist without a
          // second count query.
          limit: pageSize + 1,
          // MI-315 — the list shows ignored movements, so a group holding
          // nothing else must still be listed.
          includeIgnoredGroups: true,
          signal: controller.signal,
        },
      );
      const rows = Array.isArray(data) ? data : [];
      setHasMoreGroups(rows.length > pageSize);
      setRawRows(rows.slice(0, pageSize));
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      setError(err);
      setRawRows([]);
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
    // `ordering` is intentionally absent: it only affects client-side sorting
    // of the merged groups, so changing it must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, grouping, subgrouping, filters, filterGroups,
      groupsOperator, startDate, endDate, reloadToken]);

  useEffect(() => {
    load();
    return () => {
      abortRef.current?.abort();
      refreshAbortRef.current?.abort();
    };
  }, [load]);

  // MI-318 — the same groups, converted. A separate request from the one above
  // on purpose: with no display currency it never runs, so the grouped view
  // costs exactly what it did before.
  useEffect(() => {
    // Conversion collapses the *target* currency, so the axis normally goes.
    // When one of the two levels IS currency it is the *source* currency —
    // the bucket's key — and dropping it left those groups with no converted
    // figure at all, showing their native total under a converted parent.
    const dims = (dimensionsForLevels(grouping, subgrouping) || [])
      .filter(d => d !== 'currency' || splitsByCurrency(grouping, subgrouping));
    if (!enabled || !displayCurrency || dims.length === 0) {
      setConvertedBuckets([]);
      return undefined;
    }
    const controller = new AbortController();
    getConvertedTotal(displayCurrency, filters, startDate, endDate, filterGroups,
      groupsOperator, { signal: controller.signal, dimensions: dims })
      .then(data => setConvertedBuckets(data?.buckets || []))
      .catch(err => { if (!axios.isCancel(err)) setConvertedBuckets([]); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, displayCurrency, grouping, subgrouping, filters, filterGroups,
      groupsOperator, startDate, endDate, reloadToken, convertedToken]);

  const groups = useMemo(() => {
    if (!rawRows.length) return [];
    const groupOrdering = groupOrderingFor(grouping, ordering);
    const converting = Boolean(displayCurrency) && convertedBuckets.length > 0;

    /** Swap in the converted figure, so a multi-currency group finally has one.
     *  A group the server could not convert keeps its per-currency behaviour. */
    const convert = (entry, byKey) => {
      if (!converting || !byKey.has(entry.key)) return entry;
      const { total, totalAmount, rateSources } = byKey.get(entry.key);
      // A group already entirely in the display currency was multiplied by one.
      // Its figure is worth showing, but marking it `≈` would claim a
      // conversion that never happened.
      const alreadyInTarget =
        entry.currencies.length === 1 && entry.currencies[0] === displayCurrency;
      return {
        ...entry,
        total,
        totalAmount,
        currency: displayCurrency,
        currencies: [displayCurrency],
        isConverted: !alreadyInTarget,
        rateSources,
      };
    };

    const parentTotals = converting
      ? convertedTotalsByKey(convertedBuckets, grouping, displayCurrency)
      : new Map();
    const merged = mergeByDimension(rawRows, grouping).map(e => convert(e, parentTotals));

    // The unit an empty bucket's zero borrows: the display currency while
    // converting, else the one currency the visible set uses — and nothing at
    // all when it spans several (rev 5). An all-ignored slice counts toward
    // nothing here either.
    const setCurrencies = [...new Set(
      rawRows.filter(r => r.num_movements !== 0).map(r => r.currency),
    )];
    const zeroCurrency = displayCurrency
      || (setCurrencies.length === 1 ? setCurrencies[0] : null);

    const parents = sortMerged(
      showEmptyGroups
        ? withEmptyBuckets(merged, {
            grouping, startDate, endDate, hasMoreGroups, zeroCurrency,
            candidates: emptyCandidatesFor?.(grouping),
          })
        : merged,
      groupOrdering,
    );

    if (!subgrouping || subgrouping === GROUPING_NONE) return parents;

    // Nest: each parent keeps only the aggregate rows that belong to it.
    const belongsTo = (row, parent) => {
      const v = valueOf(row, grouping);
      return String(v) === String(parent.value)
        && (grouping !== GROUPING_MONTH || row.year === parent.year);
    };
    return parents.map(parent => {
      // A synthesized bucket has no aggregate rows behind it, so it has no
      // children either — an empty month holds no currencies.
      if (parent.isEmpty) return parent;
      const mine = rawRows.filter(row => belongsTo(row, parent));
      const childTotals = converting
        ? convertedTotalsByKey(
            convertedBuckets.filter(b => belongsTo(b, parent)),
            subgrouping, displayCurrency)
        : new Map();
      // A date subgroup runs chronologically for the same reason a date group
      // does: months ranked by amount are an unreadable sequence, and a run of
      // empty ones scattered through it says nothing at all.
      const childOrdering = isDateGrouping(subgrouping)
        ? groupOrderingFor(subgrouping, ordering)
        : groupOrdering;
      const merged = mergeByDimension(mine, subgrouping).map(e => convert(e, childTotals));
      const bounds = childBounds(parent, grouping, startDate, endDate);
      return {
        ...parent,
        children: sortMerged(
          showEmptyGroups
            ? withEmptyBuckets(merged, {
                grouping: subgrouping,
                startDate: bounds.start,
                endDate: bounds.end,
                hasMoreGroups,
                zeroCurrency,
                candidates: emptyCandidatesFor?.(subgrouping),
              })
            : merged,
          childOrdering,
        ),
      };
    });
  }, [rawRows, grouping, subgrouping, ordering, displayCurrency, convertedBuckets,
      showEmptyGroups, startDate, endDate, hasMoreGroups, emptyCandidatesFor]);

  /**
   * The same two levels, pivoted for the board (rev 5).
   *
   * A subgroup nested inside a 264px column can only be read against its own
   * column. Linear answers this by changing axes instead: the group stays the
   * column and the subgroup becomes a band across all of them, which is what
   * lets you read USD across December, November and October at a glance.
   *
   * No new request — the parent → child nesting above and this child → parent
   * pivot are two derivations of the very same `rawRows`.
   */
  const swimlanes = useMemo(() => {
    if (!subgrouping || subgrouping === GROUPING_NONE) return [];
    const lanes = new Map();
    groups.forEach(parent => {
      (parent.children || []).forEach(child => {
        let lane = lanes.get(child.key);
        if (!lane) {
          lane = { key: child.key, entries: [], cells: new Map(), child };
          lanes.set(child.key, lane);
        }
        lane.entries.push(child);
        lane.cells.set(parent.key, child);
      });
    });

    const folded = [...lanes.values()].map(lane => ({
      ...foldEntries(lane.entries, {
        key: lane.key,
        grouping: subgrouping,
        value: lane.child.value,
        label: lane.child.label,
        year: lane.child.year,
      }),
      cells: lane.cells,
    }));

    return sortMerged(folded, groupOrderingFor(subgrouping, ordering));
  }, [groups, subgrouping, ordering]);

  /** Append the next page of group headers. */
  const loadMoreGroups = useCallback(async () => {
    const dimensions = dimensionsForLevels(grouping, subgrouping);
    if (!dimensions || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await groupMovements(
        filters, dimensions, startDate, endDate, filterGroups, groupsOperator,
        granularityFor(grouping) || granularityFor(subgrouping),
        {
          orderBy: serverOrderingFor(grouping),
          limit: groupPageFor(grouping) + 1,
          offset: pagesRef.current * groupPageFor(grouping),
          includeIgnoredGroups: true,
        },
      );
      const pageSize = groupPageFor(grouping);
      const rows = Array.isArray(data) ? data : [];
      pagesRef.current += 1;
      setHasMoreGroups(rows.length > pageSize);
      setRawRows(prev => [...prev, ...rows.slice(0, pageSize)]);
    } catch {
      setHasMoreGroups(false);
    } finally {
      setLoadingMore(false);
    }
  }, [grouping, subgrouping, filters, filterGroups, groupsOperator,
      startDate, endDate, loadingMore]);

  /** Distinct currencies across the visible set — drives the amount-ordering
   *  gate, with no extra request. */
  const currencyCount = useMemo(
    () => new Set(rawRows.map(r => r.currency)).size || 1,
    [rawRows]
  );

  /** Re-read everything the headers are built from — aggregates and, when one
   *  is active, the converted figures on top of them. */
  const refetch = useCallback(() => setReloadToken(t => t + 1), []);

  /**
   * Re-read the headers on screen after an edit, without starting over.
   *
   * One request covers every page already loaded, so the groups the reader
   * scrolled to stay, and nothing is dimmed or swapped for a spinner. What
   * comes back is compared with what was there: a group whose numbers moved
   * has its version bumped and reads its rows again, and every other group is
   * left exactly as it is. `refetch` is for the changes this cannot follow.
   */
  const refreshHeaders = useCallback(async () => {
    if (!enabled) return;
    const dimensions = dimensionsForLevels(grouping, subgrouping);
    if (!dimensions) return;

    refreshAbortRef.current?.abort();
    const controller = new AbortController();
    refreshAbortRef.current = controller;
    const pages = pagesRef.current;
    const reload = reloadTokenRef.current;
    const loaded = pages * groupPageFor(grouping);
    try {
      const data = await groupMovements(
        filters, dimensions, startDate, endDate, filterGroups, groupsOperator,
        granularityFor(grouping) || granularityFor(subgrouping),
        {
          orderBy: serverOrderingFor(grouping),
          limit: loaded + 1,
          includeIgnoredGroups: true,
          signal: controller.signal,
        },
      );
      // A reload, or another page of groups, started meanwhile: it owns the
      // list now, and this read describes a list that no longer exists.
      if (reload !== reloadTokenRef.current || pages !== pagesRef.current) return;
      const rows = Array.isArray(data) ? data : [];
      const fresh = rows.slice(0, loaded);
      const touched = changedGroupKeys(rawRowsRef.current, fresh, grouping);
      setHasMoreGroups(rows.length > loaded);
      setRawRows(fresh);
      if (touched.length === 0) return;
      setGroupVersions(prev => {
        const next = { ...prev };
        touched.forEach(key => { next[key] = (next[key] || 0) + 1; });
        return next;
      });
      setConvertedToken(t => t + 1);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      // Not being able to read the headers quietly is no reason to leave them
      // wrong: fall back to reading everything.
      setReloadToken(t => t + 1);
    }
  }, [enabled, grouping, subgrouping, filters, filterGroups, groupsOperator,
      startDate, endDate]);

  return {
    groups, swimlanes, loading, error, currencyCount, refetch,
    refreshHeaders, groupVersions, generation: reloadToken,
    hasMoreGroups, loadingMore, loadMoreGroups,
  };
}
